import Schaffolding from "../../assets/pexelsStallning.jpg";
import styled, { css } from "styled-components";
import { FlexContainer } from "../WrapperElements";
import { FC, useEffect, useState, useMemo } from "react";
import { ServiceType, getServices } from "../../contentful/getServices";

export const Services: FC = () => {
  const [services, setServices] = useState<ServiceType[]>([]);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const res = await getServices();
        const sorted = res.sort((a, b) => {
          return a.educationName.localeCompare(b.educationName);
        });
        setServices(sorted);
      } catch (error) {
        console.log(error);
      }
    };

    if (!services.length) {
      fetchService();
    }
  }, [services]);

  const { featuredServices, allServices } = useMemo(() => {
    const featured = services.filter((service) => service.image && service.pdf);
    return {
      featuredServices: featured.slice(0, 6),
      allServices: services,
    };
  }, [services]);

  return (
    <FlexContainer $centeredJustify>
      <StyledOurServices>
        <StyledBigPhotos>
          <BigPhoto />
        </StyledBigPhotos>

        <InfoWrapper>
          <h2 id="services">UTBILDNINGAR</h2>

          {featuredServices.length > 0 && (
            <FeaturedSection>
              <SectionLabel>Utvalda Kurser</SectionLabel>
              <FeaturedGrid>
                {featuredServices.map((service) => (
                  <StyledLink
                    key={service.educationName}
                    href={service.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FeaturedCard>
                      <FeaturedImage
                        src={service.image}
                        alt={service.educationName}
                      />
                      <FeaturedCardTitle>
                        {service.educationName}
                      </FeaturedCardTitle>
                    </FeaturedCard>
                  </StyledLink>
                ))}
              </FeaturedGrid>
            </FeaturedSection>
          )}

          <AllCoursesSection>
            <SectionLabel>Alla Utbildningar</SectionLabel>
            <CompactGrid>
              {allServices.map((service) => (
                <CompactItem
                  key={service.educationName}
                  as={service.pdf ? "a" : "div"}
                  href={service.pdf || undefined}
                  target={service.pdf ? "_blank" : undefined}
                  rel={service.pdf ? "noopener noreferrer" : undefined}
                  $hasPdf={!!service.pdf}
                >
                  <CourseName>{service.educationName}</CourseName>
                  {service.pdf && <PdfIndicator>PDF</PdfIndicator>}
                </CompactItem>
              ))}
            </CompactGrid>
          </AllCoursesSection>

          <InfoText>
            <i>
              Alla utbildare är ackrediterade för att registrera på ID06
              kompetensdatabas. Enligt de krav som ställs under 2023.
              <br />
              Vi håller öppna utbildningar på olika platser i Sverige.
              Lärarledda webbutbildningar och repetitionsutbildningar, även
              internutbildning på plats hos kund. Begär offert för mer
              information.
            </i>
          </InfoText>
        </InfoWrapper>
      </StyledOurServices>
    </FlexContainer>
  );
};

const StyledOurServices = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 35px;
`;

const StyledBigPhotos = styled.div`
  display: flex;
  padding: 30px 0 30px 0;

  @media screen and (max-width: 950px) {
    display: none;
  }
`;

const InfoWrapper = styled.div`
  padding: var(--main-padding);
`;

const FeaturedSection = styled.section`
  margin-bottom: 60px;

  @media screen and (max-width: 950px) {
    margin-bottom: 40px;
  }
`;

const AllCoursesSection = styled.section`
  margin-bottom: 40px;
`;

const SectionLabel = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-primary-1);
  margin: 0 0 24px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media screen and (max-width: 950px) {
    font-size: 16px;
    margin: 0 0 16px 0;
  }
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media screen and (max-width: 950px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media screen and (min-width: 951px) and (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: var(--color-black);
`;

const FeaturedCard = styled.div`
  border-radius: 8px;
  height: 280px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    color: var(--color-primary-1);
    cursor: pointer;
  }

  @media screen and (max-width: 950px) {
    height: 240px;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px 8px 0 0;

  @media screen and (max-width: 950px) {
    height: 160px;
  }
`;

const FeaturedCardTitle = styled.p`
  margin: 0;
  padding: 16px;
  line-height: 22px;
  flex: 1;
  display: flex;
  align-items: center;
  font-weight: 500;

  @media screen and (max-width: 950px) {
    padding: 12px;
    font-size: 14px;
  }
`;

const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media screen and (max-width: 950px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media screen and (min-width: 951px) and (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const CompactItem = styled.div<{ $hasPdf: boolean }>`
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 64px;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  color: var(--color-black);
  background-color: var(--color-white);

  ${(p) =>
    p.$hasPdf &&
    css`
      cursor: pointer;

      &:hover {
        border-color: var(--color-primary-1);
        background-color: rgba(49, 92, 43, 0.02);
        transform: translateX(4px);
      }
    `}

  @media screen and (max-width: 950px) {
    padding: 12px;
    height: 42;
    min-height: 48px;
  }
`;

const CourseName = styled.span`
  font-size: 14px;
  line-height: 20px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media screen and (max-width: 950px) {
    font-size: 13px;
    -webkit-line-clamp: 3;
  }
`;

const PdfIndicator = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--color-white);
  background-color: var(--color-primary-1);
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  margin-top: 40px;

  @media screen and (max-width: 950px) {
    margin-top: 24px;
  }
`;

const BigPhoto = styled.div`
  background-attachment: fixed;
  background-repeat: no-repeat;
  background-image: url(${Schaffolding});
  background-position: bottom;
  background-size: cover;
  filter: url(#duoForest);
  height: 55vh;
  min-height: 500px;
  width: 100%;
`;
